import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Warning, IdentificationCard } from "@phosphor-icons/react";
import { useWorkflow } from "../workflow/WorkflowContext";
import { canonicalPeriodFx, canonicalMonthlyFx, INPUT_CHANGE_TEXT } from "../workflow/calcInputs";
import { useAppVersion, versionVerdict, fmtLocalDateTime } from "../lib/appVersion";
import { guardSupersede } from "../workflow/useGuardedSupersede";
import { isSignedOff } from "../workflow/calcStatus";   // A2-581 lead 35

/**
 * A1-93 (2) — which calculation is on screen. Shown on steps 3-6 whenever
 * a calc is loaded.
 *
 * The incident behind it: after a deploy the operator believed she had
 * recalculated, but was looking at a calculation made BEFORE the deploy —
 * a calc re-opened by reload, History or a link looked exactly like a
 * fresh one (no id, no run time, no version anywhere). This names the
 * calc, when it ran, the app version it ran under (and says plainly when
 * the app has changed since), the inputs it used, its status, and — when
 * the form no longer matches it — that it is out of date and why.
 */
const fmtDay = (d) => {
  const s = String(d || "").slice(0, 10);
  if (!s) return "—";
  const x = new Date(`${s}T00:00:00`);
  return Number.isNaN(x.getTime()) ? s
    : x.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const fxSummary = (calc, t) => {
  const period = Object.entries(canonicalPeriodFx(calc.period_fx_rates, calc.regime))
    .map(([k, v]) => { const [tgt, src] = k.split("<"); return `${src} → ${tgt} ${v}`; });
  const keys = Object.keys(canonicalMonthlyFx(calc.monthly_fx_rates));
  // A2-543: ECB rates of the day ("@2026-09-10"); A2-613: one return's own,
  // the ECB day for FR/NL/... ("@FR:2026-09-10") or Germany's BMF month ("@DE:2026-09")
  const daily = keys.filter((k) => /@([A-Z]{2}:)?\d{4}-\d{2}-\d{2}$/.test(k)).length;
  const bmf = keys.filter((k) => /@[A-Z]{2}:\d{4}-\d{2}$/.test(k)).length;
  const monthly = keys.length - daily - bmf;
  const parts = [...period];
  if (monthly) parts.push(t("ui.calc_identity.fx_monthly", { count: monthly, defaultValue: `${monthly} monthly rate(s)` }));
  if (daily) parts.push(t("ui.calc_identity.fx_daily", { count: daily, defaultValue: `${daily} ECB daily rate(s)` }));
  if (bmf) parts.push(t("ui.calc_identity.fx_bmf", { count: bmf, defaultValue: `${bmf} BMF monthly rate(s)` }));
  return parts.length ? parts.join("; ") : t("ui.calc_identity.fx_none", { defaultValue: "none entered" });
};

const Field = ({ label, testid, children }) => (
  <div className="min-w-0">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>{" "}
    <span data-testid={testid} className="font-medium text-slate-800 dark:text-slate-100 break-words">{children}</span>
  </div>
);

export const CalcIdentityHeader = () => {
  const wf = useWorkflow();
  const { calc, clients, runs, state, setActiveStep, activeStep } = wf;
  const { plan: supPlan } = guardSupersede(calc?.id, wf);
  const { t } = useTranslation();
  const nav = useNavigate();
  const appCommit = useAppVersion(calc?.id);
  if (!calc) return null;

  const verdict = versionVerdict(calc.code_commit, appCommit);
  const client = (clients || []).find((c) => c.id === calc.client_id);
  const fileNames = (calc.run_ids || []).map((id) => (runs || []).find((r) => r.id === id)?.filename || id.slice(0, 8));
  const stale = !!state?.derived?.calcStale;
  const staleInputs = state?.derived?.staleInputs || [];
  const cf = String(calc.country_filter || "ALL").toUpperCase();
  const status = calc.superseded_by_calc_id ? "superseded"
    : calc.frozen ? "frozen"
      : isSignedOff(calc) ? "signed" : "open";
  const statusText = {
    superseded: t("ui.calc_identity.status_superseded", { defaultValue: "Superseded — a newer calculation replaces this one" }),
    frozen: t("ui.calc_identity.status_frozen", { ref: calc.filing_reference || "—", defaultValue: `Locked for filing (${calc.filing_reference || "—"})` }),
    signed: t("ui.calc_identity.status_signed", { name: calc.signed_off_by || "—", defaultValue: `Signed off by ${calc.signed_off_by || "—"}` }),
    open: t("ui.calc_identity.status_open", { defaultValue: "Not signed off yet" }),
  }[status];
  // A1-93 review — the version is advice only where re-running is the
  // right action: an open (unsigned, unlocked) calc. On a signed, locked or
  // superseded calc it is information; there the record is what was filed.
  const versionAdvice = verdict === "older" && status === "open";
  const ranAt = fmtLocalDateTime(calc.created_at);
  // A locked filing is changed by superseding it, never by "running it again".
  const plan = status === "frozen" ? supPlan : null;
  const staleText = status === "superseded"
    ? t("ui.calc_identity.stale_title_superseded", { defaultValue: "What is on the form no longer matches this calculation. It has been replaced; do not file from it." })
    : plan?.blocked ? t("ui.calc_identity.supersede_blocked", { defaultValue: plan.blocked })
      : plan ? t("ui.calc_identity.stale_title_frozen", { defaultValue: "What is on the form no longer matches this locked filing. To file with it, supersede the filing on Lock & export — the replacement uses the exchange rates and country filter now on the form." })
        : t("ui.calc_identity.stale_title", { defaultValue: "Out of date — what is on the form no longer matches this calculation. Run it again before signing off or locking." });
  const warn = stale || status === "superseded" || versionAdvice;
  // A2-580 (operator 2026-10-27: "go to run calculations button that is dead"):
  // on Step 3 itself the way to re-run is the Run button on this page.
  const goRerun = () => {
    if (activeStep === 3) {
      const b = document.querySelector('[data-testid="calc-run-button"]');
      if (b) { b.scrollIntoView?.({ behavior: "smooth", block: "center" }); b.focus?.(); }
      return;
    }
    setActiveStep(3);
  };
  const rerunLabel = activeStep === 3
    ? t("ui.calc_identity.stale_goto_here", { defaultValue: "Run it again — the Run button is below ↓" })
    : t("ui.calc_identity.stale_goto", { defaultValue: "Go to Run calculation →" });

  return (
    <section
      data-testid="calc-identity-header" data-calc-id={calc.id} data-stale={stale} data-status={status}
      aria-label={t("ui.calc_identity.aria", { defaultValue: "Calculation on screen" })}
      className={`rounded-xl border px-4 py-3 text-xs space-y-2 ${warn
        ? "border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30"
        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"}`}
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <span className="flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-100">
          <IdentificationCard size={14} weight="bold" aria-hidden="true" />
          {t("ui.calc_identity.title", { defaultValue: "Calculation" })}{" "}
          <span data-testid="calc-identity-id" className="tabular-nums">{calc.id.slice(0, 8)}</span>
        </span>
        <Field label={t("ui.calc_identity.ran_at", { defaultValue: "Run on" })} testid="calc-identity-ran-at">
          {ranAt || "—"}
        </Field>
        <Field label={t("ui.calc_identity.status", { defaultValue: "Status" })} testid="calc-identity-status">{statusText}</Field>
        <Field label={t("ui.calc_identity.files", { defaultValue: "Files" })} testid="calc-identity-files">{fileNames.join(", ") || "—"}</Field>
        <Field label={t("ui.calc_identity.period", { defaultValue: "Period" })} testid="calc-identity-period">
          {fmtDay(calc.period_start)} – {fmtDay(calc.period_end)}
        </Field>
      </div>
      {/* A2-580 (operator 2026-10-27: "that massive banner ... why not hide it"):
          the rest folded; the exchange-rate list alone ran to four lines. */}
      <details data-testid="calc-identity-details" className="text-slate-600 dark:text-slate-300">
        <summary className="cursor-pointer select-none">{t("ui.calc_identity.details", { defaultValue: "Details" })}</summary>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1">
        <Field label={t("ui.calc_identity.client", { defaultValue: "Client" })} testid="calc-identity-client">
          {client?.name || calc.client_id || t("ui.calc_identity.version_unrecorded", { defaultValue: "not recorded" })}
        </Field>
        <Field label={t("ui.calc_identity.version", { defaultValue: "App version" })} testid="calc-identity-version">
          <span data-verdict={verdict} className="tabular-nums">{calc.code_commit ? String(calc.code_commit).slice(0, 7) : t("ui.calc_identity.version_unrecorded", { defaultValue: "not recorded" })}</span>
          {verdict === "older" && <span className="ml-1 text-slate-500 dark:text-slate-400">({versionAdvice
            ? t("ui.calc_identity.version_earlier", { defaultValue: "earlier version" })
            /* A2-537 U15: a frozen or replaced calc keeps the version it ran on, on purpose */
            : t("ui.calc_identity.version_earlier_kept", { defaultValue: "earlier version — a closed calc keeps the version it ran on" })})</span>}
        </Field>
        <Field label={t("ui.calc_identity.fx", { defaultValue: "Exchange rates" })} testid="calc-identity-fx">{fxSummary(calc, t)}</Field>
        <Field label={t("ui.calc_identity.countries", { defaultValue: "Countries" })} testid="calc-identity-country">
          {cf === "ALL" ? t("ui.calc_identity.countries_all", { defaultValue: "all" }) : cf}
        </Field>
        </div>
      </details>
      {versionAdvice && (
        <div className="flex flex-wrap items-center gap-2">
          <p data-testid="calc-identity-version-warning" role="status" className="flex items-center gap-1.5 font-semibold text-amber-900 dark:text-amber-100">
            <Warning size={14} weight="bold" aria-hidden="true" />
            {t("ui.calc_identity.version_warning", { when: ranAt || "—", defaultValue: `Calculated on an earlier version of the app (${ranAt || "—"}). Run it again to use the current version.` })}
          </p>
          {!stale && (
            <button type="button" data-testid="calc-version-goto-rerun" onClick={goRerun}
              className="underline font-semibold text-amber-900 dark:text-amber-100">{rerunLabel}</button>
          )}
        </div>
      )}
      {status === "superseded" && (
        <div className="flex flex-wrap items-center gap-3 text-amber-900 dark:text-amber-100">
          <span data-testid="calc-identity-superseded">{t("ui.calc_identity.superseded_body", { defaultValue: "Kept for the audit trail. Do not file from it." })}</span>
          <button type="button" data-testid="calc-identity-open-current"
            onClick={() => nav(`/?calc_id=${calc.superseded_by_calc_id}`)}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg">
            {t("ui.calc_identity.open_current", { defaultValue: "Open the current calculation →" })}
          </button>
        </div>
      )}
      {stale && (
        <div data-testid="calc-stale-banner" role="alert" className="border-t border-amber-300 dark:border-amber-700 pt-2 text-amber-900 dark:text-amber-100 space-y-1">
          <div data-testid="calc-stale-title" className="font-semibold">{staleText}</div>
          <ul className="list-disc pl-5">
            {staleInputs.map((k) => (
              <li key={k} data-testid="calc-stale-reason" data-input={k}>
                {t(`ui.calc_identity.stale_reason.${k}`, { defaultValue: INPUT_CHANGE_TEXT[k] })}
              </li>
            ))}
          </ul>
          {plan && !plan.blocked ? (
            <button type="button" data-testid="calc-stale-goto-supersede" onClick={() => setActiveStep(6)}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg">
              {t("ui.calc_identity.stale_goto_supersede", { defaultValue: "Go to Lock & export →" })}
            </button>
          ) : plan?.blocked ? (   /* A2-581 lead 14: a locked filing whose supersede
              is blocked - the reason above says to put the inputs back on Run
              calculation; a re-run there would only make a separate calculation */
            <button type="button" data-testid="calc-stale-goto-fix-inputs" onClick={goRerun}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg">
              {t("ui.calc_identity.stale_goto_fix_inputs", { defaultValue: "Put the inputs back on Run calculation →" })}
            </button>
          ) : status !== "superseded" && (
            <button type="button" data-testid="calc-stale-goto-rerun" onClick={goRerun}
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg">
              {rerunLabel}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default CalcIdentityHeader;
